import { useEffect, useRef, useState } from 'react';
import type { CloseScope } from './dialogBackTrail';

/**
 * Close-guard for the fullscreen record dialogs: closing (Esc, the X, Cancel)
 * or leaving (browser back, tab close) must not silently drop unsaved edits.
 *
 * Route every close through `requestClose` — a dirty form gets the confirm
 * dialog (render `UnsavedChangesDialog` with the returned props), a clean one
 * closes straight away. Browser back is intercepted with a history sentinel
 * pushed while the dialog is open: popping it closes a clean dialog (back =
 * close, as on mobile) and asks on a dirty one.
 *
 * Two ways out, told apart for the routed dialogs (see `useDialogClose`):
 * `requestClose` leaves the whole chain of linked dialogs, `requestBack` — and
 * the browser's back button — only this one. A save returns the way the user
 * asked to leave, or one step when they just saved.
 */
export function useUnsavedCloseGuard({
    open,
    isDirty,
    onClose,
    onSave
}: {
    open: boolean;
    /** Unsaved edits — react-hook-form's `formState.isDirty`. */
    isDirty: boolean;
    /** Actually close (the parent's handler), as opposed to asking first. */
    onClose: (scope: CloseScope) => void;
    /**
     * Submit the form. A successful save closes the dialog itself; a validation
     * failure leaves it open showing errors.
     */
    onSave: () => Promise<void>;
}) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    // How far the exit the confirm dialog is asking about would go.
    const pendingScopeRef = useRef<CloseScope | null>(null);

    // The popstate handler must see the current values without re-subscribing
    // (re-pushing the sentinel) on every keystroke.
    const isDirtyRef = useRef(isDirty);
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        isDirtyRef.current = isDirty;
        onCloseRef.current = onClose;
    });

    // Browser back: one sentinel entry per dialog-open, so the first "back"
    // lands in our handler instead of leaving the page.
    const sentinelRef = useRef(false);
    // Set once a close is underway: every further popstate (e.g. the route
    // entry popping after the sentinel) belongs to that close, not the user.
    const closedRef = useRef(false);

    // The single exit: marks the guard closed (silencing its popstate handler)
    // and delegates to the host's onClose, which may flip open-state or
    // navigate a dialog ROUTE away.
    function performClose(scope: CloseScope) {
        if (closedRef.current) {
            return;
        }
        closedRef.current = true;
        onCloseRef.current(scope);
    }
    /**
     * The sentinel-removing back() scheduled by the cleanup. It MUST be
     * deferred: StrictMode re-runs the effect immediately, and Chrome resolves
     * a back target at CALL time, so an inline back() races the re-run's
     * pushState, skips both sentinels and reads as "the user left".
     */
    const pendingBackRef = useRef<number | null>(null);
    useEffect(() => {
        if (!open) {
            return;
        }
        /**
         * A fresh dialog session. Cleared HERE, not only in performClose: a
         * dialog that merely flips `open` instead of unmounting keeps its refs
         * across opens, and a leftover `true` silences every later close —
         * Save would write the entry but leave the dialog standing.
         */
        closedRef.current = false;
        if (pendingBackRef.current !== null) {
            // Effect re-run (StrictMode) before the scheduled back() fired:
            // the sentinel is still on the stack — keep it, push nothing.
            window.clearTimeout(pendingBackRef.current);
            pendingBackRef.current = null;
        } else {
            window.history.pushState({ dialogGuard: true }, '');
        }
        sentinelRef.current = true;
        function onPopState(event: PopStateEvent) {
            if (closedRef.current) {
                // Pops caused by the close itself (sentinel and/or dialog
                // route leaving the stack) — not the user.
                return;
            }
            if (
                (event.state as { dialogGuard?: boolean } | null)?.dialogGuard
            ) {
                // Landed ON a sentinel (e.g. a previous dialog's deferred
                // back() popping onto ours) — not the user leaving. Stay put.
                sentinelRef.current = true;
                return;
            }
            sentinelRef.current = false;
            if (isDirtyRef.current) {
                // Stay on the page: restore the sentinel and ask.
                window.history.pushState({ dialogGuard: true }, '');
                sentinelRef.current = true;
                pendingScopeRef.current = 'step';
                setConfirmOpen(true);
            } else {
                performClose('step');
            }
        }
        window.addEventListener('popstate', onPopState);
        return () => {
            window.removeEventListener('popstate', onPopState);
            /**
             * Closed by other means — take the sentinel off the stack so the
             * next back doesn't need a second press. Checked against the LIVE
             * history entry: a routed close has already stepped past the
             * sentinel, and a stray back() would re-enter the dialog route.
             */
            const onSentinel = Boolean(
                (window.history.state as { dialogGuard?: boolean } | null)
                    ?.dialogGuard
            );
            if (sentinelRef.current && onSentinel) {
                sentinelRef.current = false;
                pendingBackRef.current = window.setTimeout(() => {
                    pendingBackRef.current = null;
                    window.history.back();
                }, 0);
            }
        };
    }, [open]);

    // Tab close / reload with unsaved edits: the browser's native prompt.
    useEffect(() => {
        if (!(open && isDirty)) {
            return;
        }
        function onBeforeUnload(event: BeforeUnloadEvent) {
            event.preventDefault();
        }
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
        };
    }, [open, isDirty]);

    function request(scope: CloseScope) {
        if (isDirty) {
            pendingScopeRef.current = scope;
            setConfirmOpen(true);
        } else {
            performClose(scope);
        }
    }

    return {
        /** Schließen, Abbrechen, Esc: out to the list the chain began on. */
        requestClose: () => request('chain'),
        /** The back crumb: to the dialog this one was reached from. */
        requestBack: () => request('step'),
        /**
         * The guard-aware close — dialogs must use THIS, not the raw onClose
         * prop, after a successful save, so a route-owned dialog steps over the
         * history sentinel exactly once.
         */
        close: () => performClose(pendingScopeRef.current ?? 'step'),
        confirmProps: {
            open: confirmOpen,
            onKeepEditing: () => {
                pendingScopeRef.current = null;
                setConfirmOpen(false);
            },
            onDiscard: () => {
                setConfirmOpen(false);
                performClose(pendingScopeRef.current ?? 'chain');
            },
            onSave: async () => {
                setConfirmOpen(false);
                await onSave();
                // Still here when validation blocked the save; a later plain
                // save must not inherit this exit.
                pendingScopeRef.current = null;
            }
        }
    };
}
